import {
  Box,
  Button,
  Chip,
  Collapse,
  makeStyles,
  TextField,
  Typography,
} from '@material-ui/core';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CancelIcon from '@material-ui/icons/Cancel';
import ThumbDownAltOutlinedIcon from '@material-ui/icons/ThumbDownAltOutlined';
import ThumbUpAltOutlinedIcon from '@material-ui/icons/ThumbUpAltOutlined';
import * as React from 'react';

// Reasons a result is a bad match. Ordered by the failure modes recruiter
// feedback surfaced most — occupation / career path / focus / recency first,
// then skills, sector, level. "Wrong focus/emphasis" captures the candidate
// whose occupation is right-ish but the match latched onto the wrong part of a
// multi-faceted CV ("too marketing-focused", "wrong aspect") — the pilot's
// second-biggest failure, previously buried under "Wrong skills". "I know this
// candidate" captures a rejection based on outside knowledge (already placed,
// unavailable, salary, etc.) rather than the match itself, so that feedback can
// be set aside when judging the model.
const BAD_FEEDBACK_REASONS = [
  'Different occupation',
  'Wrong career path',
  'Wrong focus/emphasis',
  'Experience not recent',
  'Wrong skills',
  'Wrong sector',
  'Wrong scope/seniority',
  'Over/underqualified',
  'I know this candidate',
];

export type Feedback =
  | { kind: 'good' }
  | { kind: 'bad'; reasons: string[]; note?: string };

const useStyles = makeStyles((theme) => ({
  wrap: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
  },
  goodButton: {
    minHeight: 36,
  },
  badButton: {
    minHeight: 36,
  },
  recorded: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    padding: theme.spacing(0.75, 1.25),
    borderRadius: 18,
    fontWeight: 600,
  },
  recordedGood: {
    backgroundColor: '#E8F5E9',
    color: theme.palette.success.dark,
  },
  recordedBad: {
    backgroundColor: '#FDECEA',
    color: theme.palette.danger.dark,
  },
  undoButton: {
    fontSize: 11,
    textTransform: 'none',
    padding: 0,
    minWidth: 0,
  },
  badPanel: {
    marginTop: theme.spacing(0.5),
    padding: theme.spacing(1.25),
    borderRadius: 4,
    backgroundColor: '#FAFAFA',
    border: '1px solid #ECECEC',
  },
  chipRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
    marginBottom: theme.spacing(1),
  },
  chip: {
    cursor: 'pointer',
  },
  panelActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
}));

interface Props {
  feedback: Feedback | undefined;
  onChange: (next: Feedback | undefined) => void;
  // vacancy results have their own reasons
  reasons?: string[];
}

export const FeedbackControls: React.FC<Props> = ({
  feedback,
  onChange,
  reasons = BAD_FEEDBACK_REASONS,
}) => {
  const classes = useStyles();
  const [badOpen, setBadOpen] = React.useState(false);
  const [draftReasons, setDraftReasons] = React.useState<string[]>([]);
  const [draftNote, setDraftNote] = React.useState('');

  React.useEffect(() => {
    if (feedback?.kind === 'bad') {
      setDraftReasons(feedback.reasons);
      setDraftNote(feedback.note ?? '');
    }
  }, [feedback]);

  if (feedback) {
    return (
      <Box className={classes.row}>
        <Box
          className={
            feedback.kind === 'good'
              ? `${classes.recorded} ${classes.recordedGood}`
              : `${classes.recorded} ${classes.recordedBad}`
          }
        >
          {feedback.kind === 'good' ? (
            <>
              <CheckCircleIcon fontSize="small" />
              <Typography variant="caption" style={{ fontWeight: 600 }}>
                Marked good — saved
              </Typography>
            </>
          ) : (
            <>
              <CancelIcon fontSize="small" />
              <Typography variant="caption" style={{ fontWeight: 600 }}>
                Marked bad
                {feedback.reasons.length
                  ? ` · ${feedback.reasons.join(', ')}`
                  : ''}
              </Typography>
            </>
          )}
        </Box>
        <Button
          className={classes.undoButton}
          size="small"
          onClick={() => {
            setBadOpen(false);
            onChange(undefined);
          }}
        >
          Undo
        </Button>
      </Box>
    );
  }

  function toggleReason(r: string) {
    setDraftReasons((prev) =>
      prev.includes(r) ? prev.filter((x) => x !== r) : [...prev, r],
    );
  }

  function saveBad() {
    onChange({
      kind: 'bad',
      reasons: draftReasons,
      note: draftNote || undefined,
    });
    setBadOpen(false);
  }

  return (
    <Box className={classes.wrap}>
      <Box className={classes.row}>
        <Button
          className={classes.goodButton}
          variant="outlined"
          size="small"
          startIcon={<ThumbUpAltOutlinedIcon />}
          onClick={() => onChange({ kind: 'good' })}
        >
          Good match
        </Button>
        <Button
          className={classes.badButton}
          variant="outlined"
          size="small"
          startIcon={<ThumbDownAltOutlinedIcon />}
          onClick={() => setBadOpen((x) => !x)}
        >
          Bad match
        </Button>
      </Box>
      <Collapse in={badOpen} unmountOnExit>
        <Box className={classes.badPanel}>
          <Typography variant="caption" color="textSecondary">
            Why is this a bad match? (optional)
          </Typography>
          <Box className={classes.chipRow} mt={0.5}>
            {reasons.map((r) => (
              <Chip
                key={r}
                label={r}
                size="small"
                className={classes.chip}
                color={draftReasons.includes(r) ? 'primary' : 'default'}
                variant={draftReasons.includes(r) ? 'default' : 'outlined'}
                onClick={() => toggleReason(r)}
              />
            ))}
          </Box>
          <TextField
            fullWidth
            multiline
            minRows={2}
            size="small"
            variant="outlined"
            placeholder="Notes (optional)"
            value={draftNote}
            onChange={(e) => setDraftNote(e.currentTarget.value)}
          />
          <Box className={classes.panelActions}>
            <Button size="small" onClick={() => setBadOpen(false)}>
              Cancel
            </Button>
            <Button
              size="small"
              variant="contained"
              color="primary"
              onClick={saveBad}
            >
              Save feedback
            </Button>
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
};
